import { useState, useMemo, useCallback } from "react";
import { getStageConfig } from "@/data/workflowEngine";
import { validateStatusWrite } from "@/data/workflowAudit";
import { useJobsQuery } from "@/hooks/useJobsQuery";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { format, parseISO } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Package, AlertTriangle, Clock, Truck, CheckCircle, Boxes, Phone,
  Camera, Shield, Activity, ChevronRight, XCircle, HelpCircle, ImageIcon,
} from "lucide-react";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import type { Job } from "@/data/types";
import type { VendorType } from "@/data/skuMapping";
import { useCatalog } from "@/data/CatalogContext";
import { JobService } from "@/services/job.service";
import { analyzeIntegrity } from "@/data/warehouseWorkflowEngine";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

const TODAY = new Date();
const TODAY_STR = format(TODAY, "yyyy-MM-dd");
const DAY_MS = 86_400_000;

/* ── Lane definitions ── */
type WarehouseLane = "ordered" | "receiving" | "staging" | "ready";

const LANE_CONFIG: Record<WarehouseLane, { label: string; sublabel: string; icon: React.ReactNode; badgeColor: string }> = {
  ordered: {
    label: "Ordered",
    sublabel: "Materials ordered — awaiting delivery",
    icon: <Truck className="h-3.5 w-3.5 text-primary" />,
    badgeColor: "text-primary bg-primary/10",
  },
  receiving: {
    label: "Receiving",
    sublabel: "Materials arrived — confirm receipt",
    icon: <Package className="h-3.5 w-3.5 text-warning" />,
    badgeColor: "text-warning bg-warning/10",
  },
  staging: {
    label: "Staging",
    sublabel: "Received materials — stage for job",
    icon: <Boxes className="h-3.5 w-3.5 text-accent-foreground" />,
    badgeColor: "text-accent-foreground bg-accent/20",
  },
  ready: {
    label: "Ready for Install",
    sublabel: "Materials staged — ready for scheduling",
    icon: <CheckCircle className="h-3.5 w-3.5 text-success" />,
    badgeColor: "text-success bg-success/10",
  },
};

const LANE_ORDER: WarehouseLane[] = ["ordered", "receiving", "staging", "ready"];

/* ── ETA helpers ── */
type EtaRisk = "safe" | "same-day" | "late" | "unknown";

const ETA_RISK_CONFIG: Record<EtaRisk, { label: string; color: string; bg: string; border: string }> = {
  safe: { label: "On Track", color: "text-success", bg: "bg-success/5", border: "border-success/15" },
  "same-day": { label: "Same Day", color: "text-warning", bg: "bg-warning/5", border: "border-warning/15" },
  late: { label: "Overdue", color: "text-destructive", bg: "bg-destructive/5", border: "border-destructive/15" },
  unknown: { label: "No ETA", color: "text-muted-foreground", bg: "bg-muted/5", border: "border-border/30" },
};

interface WarehouseCard {
  id: string;
  job: Job;
  customerCity: string;
  productLabel: string;
  lane: WarehouseLane;
  installTiming: string;
  daysUntil: number;
  revenueAtRisk: number;
  phone: string;
  vendorName: string;
  vendorType: VendorType | null;
  etaLabel: string | null;
  etaRisk: EtaRisk;
}

function computeEta(job: Job): { etaLabel: string | null; etaRisk: EtaRisk } {
  let eta: string | null = null;
  if (job.orderDetail?.length) {
    const arrivals = job.orderDetail.map(o => o.confirmedArrival || o.expectedArrival).filter(Boolean).sort();
    if (arrivals.length) eta = arrivals[arrivals.length - 1];
  }
  if (!eta) return { etaLabel: null, etaRisk: "unknown" };

  const etaParsed = parseISO(eta);
  const days = Math.round((etaParsed.getTime() - TODAY.getTime()) / DAY_MS);
  let etaLabel: string;
  if (days < 0) etaLabel = `Arrived ${Math.abs(days)}d ago`;
  else if (days === 0) etaLabel = "Arrives Today";
  else if (days === 1) etaLabel = "Arrives Tomorrow";
  else if (days <= 7) etaLabel = `Arrives in ${days}d`;
  else etaLabel = `ETA ${format(etaParsed, "MMM d")}`;

  let etaRisk: EtaRisk = "unknown";
  if (job.scheduledInstallDate) {
    const installDays = Math.round((parseISO(job.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS);
    if (days > installDays) etaRisk = "late";
    else if (days >= installDays - 1) etaRisk = "same-day";
    else etaRisk = "safe";
  } else {
    etaRisk = days < 0 ? "late" : days === 0 ? "same-day" : "safe";
  }
  return { etaLabel, etaRisk };
}

/* ── Receiving item status types ── */
type ReceivingItemStatus = "pending" | "received" | "missing" | "incorrect";
type IncorrectReason = "wrong_item" | "wrong_size" | "damaged" | "other";

interface ReceivingLineItem {
  id: string;
  name: string;
  category: string;
  sku: string;
  expectedQty: number;
  referenceImageUrl: string;
  status: ReceivingItemStatus;
  incorrectReason?: IncorrectReason;
}

function deriveReceivingItems(job: Job, catalog: any[]): ReceivingLineItem[] {
  const items: ReceivingLineItem[] = [];
  const config = job.projectConfig;
  if (!config) return items;

  const resolveName = (id: string | null | undefined): string => {
    if (!id) return "";
    const found = catalog.find(p => p.id === id);
    return found?.displayName || id;
  };

  const findVariantImage = (name: string): string => {
    if (!name) return "";
    for (const p of catalog) {
      for (const v of (p.variants || [])) {
        if (v.name === name || p.displayName === name) return v.referenceImageUrl || v.imageUrl || "";
      }
    }
    return "";
  };

  const findSku = (search: string): string => {
    if (!search) return "";
    const found = catalog.find((p) => p.displayName.toLowerCase().includes(search.toLowerCase()));
    const variant = found?.variants?.[0];
    return variant?.sku || "";
  };

  // Wall System
  const wallMaterial = config.bathroomMaterials?.[0] || config.primaryMaterial || config.selectedWallSystem || "";
  if (wallMaterial) {
    const wallColor = config.colorSelection || config.wallColor || config.bathroomMaterials?.[1] || "";
    items.push({
      id: `ri-wall-${job.id}`,
      name: wallColor ? `${wallMaterial} — ${wallColor}` : wallMaterial,
      category: "Wall System",
      sku: findSku(wallMaterial),
      expectedQty: 1,
      referenceImageUrl: findVariantImage(wallColor || wallMaterial),
      status: "pending",
    });
  }

   // Base
   const base = config.baseType || (config.selectedBase ? resolveName(config.selectedBase) : "") || "";
   if (base) {
     items.push({
       id: `ri-base-${job.id}`,
       name: base,
       category: "Base System",
       sku: findSku("base") || (config.selectedBase ? findSku(base) : ""),
       expectedQty: 1,
       referenceImageUrl: findVariantImage(base),
       status: "pending",
     });
   }

   // Enclosure
   const enclosure = config.enclosureType || (config.selectedGlass ? resolveName(config.selectedGlass) : "") || "";
   if (enclosure) {
     items.push({
       id: `ri-enc-${job.id}`,
       name: enclosure,
       category: "Enclosure",
       sku: findSku("glass") || findSku("enclosure") || (config.selectedGlass ? findSku(enclosure) : ""),
       expectedQty: 1,
       referenceImageUrl: findVariantImage(enclosure),
       status: "pending",
     });
   }

   // Fixtures
   const fixture = config.selectedFixture ? resolveName(config.selectedFixture) : "";
   if (fixture) {
     items.push({
       id: `ri-fix-${job.id}`,
       name: fixture,
       category: "Fixtures",
       sku: findSku("fixture") || findSku(fixture),
       expectedQty: 1,
       referenceImageUrl: findVariantImage(fixture),
       status: "pending",
     });
   }

   // Hardware
   const hw = config.hardwareFinish || "";
   if (hw) {
     items.push({
       id: `ri-hw-${job.id}`,
       name: `Hardware — ${hw}`,
       category: "Hardware",
       sku: findSku("hardware"),
       expectedQty: 1,
       referenceImageUrl: "",
       status: "pending",
     });
   }

   // Grab bars
   const grabBars = config.selectedGrabBars || [];
   if (grabBars.length > 0) {
     grabBars.forEach((gb, i) => {
       const gbName = resolveName(gb);
       items.push({
         id: `ri-gb-${job.id}-${i}`,
         name: gbName,
         category: "Accessories",
         sku: gbName ? findSku(gbName) : "",
         expectedQty: 1,
         referenceImageUrl: gbName ? findVariantImage(gbName) : "",
         status: "pending",
       });
     });
   }

   // Add-ons
   const addons = config.selectedAddOns || config.bathroomPhase2Addons || [];
   if (addons.length > 0) {
     addons.forEach((a, i) => {
       const aName = resolveName(a);
       items.push({
         id: `ri-addon-${job.id}-${i}`,
         name: aName,
         category: "Add-ons",
         sku: aName ? findSku(aName) : "",
         expectedQty: 1,
         referenceImageUrl: aName ? findVariantImage(aName) : "",
         status: "pending",
       });
     });
   }

  // Fallback: if nothing derived, create generic items from materials list
  if (items.length === 0 && job.materialsList?.length) {
    job.materialsList.forEach((m, i) => {
      items.push({
        id: `ri-mat-${job.id}-${i}`,
        name: m.itemName,
        category: "Material",
        sku: m.sku,
        expectedQty: m.quantityRequired,
        referenceImageUrl: "",
        status: "pending",
      });
    });
  }

  return items;
}

const INCORRECT_REASONS: { value: IncorrectReason; label: string }[] = [
  { value: "wrong_item", label: "Wrong item" },
  { value: "wrong_size", label: "Wrong size" },
  { value: "damaged", label: "Damaged" },
  { value: "other", label: "Other" },
];

/* ── Component ── */
export function WarehouseCommandCenter() {
  const { jobs: allJobs = [] } = useJobsQuery({});
  const { products } = useCatalog();
  const { skin } = useUISkin();
  const isCockpit = skin === "cockpit";
  const [callConfirm, setCallConfirm] = useState<{ name: string; phone: string } | null>(null);
  const [receivingModal, setReceivingModal] = useState<{ job: Job } | null>(null);
  const [receivingItems, setReceivingItems] = useState<ReceivingLineItem[]>([]);
  const [receivingNotes, setReceivingNotes] = useState("");

  const openJobs = useMemo(() => allJobs.filter(j => (getStageConfig(j.currentStage)?.order ?? 0) < 100), [allJobs]);

  /* ── KPI Metrics ── */
  const metrics = useMemo(() => {
    const ordered = openJobs.filter(j => j.materialReadiness === "Ordered").length;
    const partial = openJobs.filter(j => j.materialReadiness === "Partially Received").length;
    const received = openJobs.filter(j => j.materialReadiness === "Fully Received").length;
    const staged = openJobs.filter(j => j.materialReadiness === "Staged for Job").length;
    const ready = openJobs.filter(j => j.materialReadiness === "Loaded for Crew").length;
    return { ordered, partial, received, staged, ready, total: ordered + partial + received + staged + ready };
  }, [openJobs]);

  /* ── Build cards ── */
  const cards = useMemo(() => {
    const result: WarehouseCard[] = [];

    openJobs.forEach(j => {
      let lane: WarehouseLane | null = null;
      if (j.materialReadiness === "Ordered" || j.materialReadiness === "Partially Received") lane = "ordered";
      else if (j.materialReadiness === "Fully Received") lane = "receiving";
      else if (j.materialReadiness === "Staged for Job") lane = "staging";
      else if (j.materialReadiness === "Loaded for Crew") lane = "ready";
      if (!lane) return;

      const material = j.projectConfig?.bathroomMaterials?.[0] ?? j.projectConfig?.primaryMaterial ?? "";
      const pt = j.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : j.projectType;
      const productLabel = [material, pt].filter(Boolean).join(" ");

      let daysUntil = -999;
      let installTiming = "Not Scheduled";
      if (j.scheduledInstallDate) {
        daysUntil = Math.round((parseISO(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS);
        if (j.scheduledInstallDate === TODAY_STR) installTiming = "Install Today";
        else if (daysUntil === 1) installTiming = "Install Tomorrow";
        else if (daysUntil >= 0) installTiming = `Installs in ${daysUntil}d`;
      }

      const matchedProduct = products.find((p) =>
        (material && p.displayName.toLowerCase().includes(material.toLowerCase())) ||
        p.displayName.toLowerCase().includes(j.projectType.toLowerCase()),
      );
      const { etaLabel, etaRisk } = computeEta(j);

      result.push({
        id: `wh-${j.id}`,
        job: j,
        customerCity: `${j.customerName} — ${j.address.city || "Unknown"}`,
        productLabel,
        lane,
        installTiming,
        daysUntil,
        revenueAtRisk: getJobDisplayValue(j),
        phone: j.phone || "",
        vendorName: j.selectedVendor || matchedProduct?.vendor || "—",
        vendorType: null,
        etaLabel,
        etaRisk,
      });
    });

    result.sort((a, b) => {
      const lo = LANE_ORDER.indexOf(a.lane) - LANE_ORDER.indexOf(b.lane);
      if (lo !== 0) return lo;
      return a.daysUntil - b.daysUntil;
    });
    return result;
  }, [openJobs, products]);

  const lanes = useMemo(() => {
    const l: Record<WarehouseLane, WarehouseCard[]> = { ordered: [], receiving: [], staging: [], ready: [] };
    cards.forEach(c => l[c.lane].push(c));
    return l;
  }, [cards]);

  /* ── Receiving sub-groups ── */
  const receivingSubGroups = useMemo(() => {
    const incoming = lanes.ordered.filter(c => c.job.materialReadiness === "Ordered");
    const partial = lanes.ordered.filter(c => c.job.materialReadiness === "Partially Received");
    return { incoming, partial };
  }, [lanes]);

  /* ── Integrity ── */
  const integrity = useMemo(() => analyzeIntegrity([], TODAY_STR), []);

  /* ── Handlers ── */
  const handleOpenReceiving = useCallback((job: Job) => {
    const items = deriveReceivingItems(job, products);
    setReceivingItems(items);
    setReceivingModal({ job });
    setReceivingNotes("");
  }, [products]);

  const handleSetItemStatus = useCallback((itemId: string, status: ReceivingItemStatus) => {
    setReceivingItems(prev => prev.map(i => i.id === itemId ? { ...i, status, incorrectReason: status === "incorrect" ? i.incorrectReason : undefined } : i));
  }, []);

  const handleSetIncorrectReason = useCallback((itemId: string, reason: IncorrectReason) => {
    setReceivingItems(prev => prev.map(i => i.id === itemId ? { ...i, incorrectReason: reason } : i));
  }, []);

  const allItemsReviewed = receivingItems.length > 0 && receivingItems.every(i => i.status !== "pending");
  const hasIssues = receivingItems.some(i => i.status === "missing" || i.status === "incorrect");
  const allReceived = allItemsReviewed && !hasIssues;

  const handleConfirmReceived = useCallback((partial: boolean) => {
    if (!receivingModal) return;
    const job = receivingModal.job;
    const newReadiness = partial ? "Partially Received" : "Fully Received";
    // Validate status ownership
    const readinessCheck = validateStatusWrite("warehouse", "materialReadiness", newReadiness);
    if (!readinessCheck.valid) {
      console.warn("[Warehouse] Blocked write:", readinessCheck.violation);
      toast.error("Status write blocked — contact admin");
      return;
    }
    JobService.update(job.id, { material_readiness: newReadiness } as Parameters<typeof JobService.update>[1]).catch(err =>
      console.error("[Warehouse] Failed to persist materialReadiness:", err)
    );
    toast.success(partial ? `Partially received — ${job.customerName}` : `Fully received — ${job.customerName}`, {
      description: partial ? "Job remains in Receiving" : "Moved to Staging",
    });
    setReceivingModal(null);
    setReceivingItems([]);
  }, [receivingModal, receivingNotes]);

  const handleMarkStaged = useCallback((job: Job) => {
    // Validate all status writes
    const checks = [
      validateStatusWrite("warehouse", "materialReadiness", "Staged for Job"),
      validateStatusWrite("warehouse", "currentStage", "Ready for Scheduling"),
    ];
    const blocked = checks.find(c => !c.valid);
    if (blocked) {
      console.warn("[Warehouse] Blocked write:", blocked.violation);
    }
    JobService.update(job.id, {
      material_readiness: "Staged for Job",
      current_stage: "Ready for Scheduling",
    } as Parameters<typeof JobService.update>[1]).catch(err =>
      console.error("[Warehouse] Failed to persist staged status:", err)
    );
    toast.success(`Staged for install — ${job.customerName}`, { description: "Moved to Scheduling Queue" });
  }, []);

  /* ── Card renderer ── */
  const renderCard = (card: WarehouseCard) => {
    const laneCfg = LANE_CONFIG[card.lane];
    return (
      <TooltipProvider key={card.id} delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="rounded-lg border border-border/40 bg-card/60 hover:border-border/60 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md">
              <div className="px-4 py-3.5 space-y-2">
                {/* Row 1: Name + Phone */}
                <div className="flex items-center gap-2.5">
                  <p className="text-[14px] font-bold text-foreground leading-tight truncate">{card.customerCity}</p>
                  {card.phone && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCallConfirm({ name: card.job.customerName, phone: card.phone }); }}
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-primary hover:underline shrink-0 transition-colors"
                    >
                      <Phone className="h-3 w-3" /> {card.phone}
                    </button>
                  )}
                </div>

                {/* Row 2: Product + Vendor */}
                <div className="flex items-center gap-2">
                  {card.productLabel && <span className="text-[12px] text-muted-foreground/60">{card.productLabel}</span>}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${laneCfg.badgeColor}`}>
                    {laneCfg.label}
                  </span>
                </div>
                {card.vendorName && card.vendorName !== "—" && (
                  <p className="text-[10px] text-muted-foreground/50">
                    → {card.vendorName}
                    {card.vendorType && (
                      <span className={`ml-1 ${card.vendorType === "Primary" ? "text-success/70" : card.vendorType === "Field" ? "text-destructive/70" : "text-warning/70"}`}>
                        ({card.vendorType})
                      </span>
                    )}
                  </p>
                )}

                {/* ETA for ordered */}
                {card.lane === "ordered" && card.etaLabel && (
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-semibold ${ETA_RISK_CONFIG[card.etaRisk].bg} ${ETA_RISK_CONFIG[card.etaRisk].border} border`}>
                    <Clock className={`h-3 w-3 ${ETA_RISK_CONFIG[card.etaRisk].color}`} />
                    <span className={ETA_RISK_CONFIG[card.etaRisk].color}>{card.etaLabel}</span>
                    <span className="text-muted-foreground/50 mx-0.5">·</span>
                    <span className={ETA_RISK_CONFIG[card.etaRisk].color}>{ETA_RISK_CONFIG[card.etaRisk].label}</span>
                  </div>
                )}
                {card.lane === "ordered" && !card.etaLabel && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-semibold bg-muted/10 border border-border/30">
                    <Clock className="h-3 w-3 text-muted-foreground/50" />
                    <span className="text-muted-foreground/60">No ETA available</span>
                  </div>
                )}

                {/* Row 3: Timing + $ + Action */}
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-[11px] text-muted-foreground/50">
                    {card.installTiming} · <span className="font-semibold text-muted-foreground/70">${card.revenueAtRisk.toLocaleString()}</span>
                  </span>
                  <div className="flex items-center gap-1.5">
                    {card.lane === "ordered" && (
                      <Button size="sm" variant="outline"
                        className="h-7 text-[11px] font-semibold px-3 border-primary/30 text-primary hover:bg-primary/10"
                        onClick={(e) => { e.stopPropagation(); handleOpenReceiving(card.job); }}>
                        <Package className="h-3 w-3 mr-1" /> Verify Materials
                      </Button>
                    )}
                    {card.lane === "receiving" && (
                      <Button size="sm" variant="outline"
                        className="h-7 text-[11px] font-semibold px-3 border-primary/30 text-primary hover:bg-primary/10"
                        onClick={(e) => { e.stopPropagation(); handleOpenReceiving(card.job); }}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Verify Materials
                      </Button>
                    )}
                    {card.lane === "staging" && (
                      <Button size="sm" variant="outline"
                        className="h-7 text-[11px] font-semibold px-3 border-success/30 text-success hover:bg-success/10"
                        onClick={(e) => { e.stopPropagation(); handleMarkStaged(card.job); }}>
                        <Shield className="h-3 w-3 mr-1" /> Mark Ready for Install
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-xs font-semibold mb-1">{card.customerCity}</p>
            <p className="text-[11px] text-muted-foreground">{card.productLabel} · ${card.revenueAtRisk.toLocaleString()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const cockpitWhKpis: StatItem[] = [
    { label: "Ordered", value: metrics.ordered },
    { label: "Partial", value: metrics.partial, accent: metrics.partial > 0 ? "warn" : "good" },
    { label: "Received", value: metrics.received },
    { label: "Staging", value: metrics.staged },
    { label: "Ready for Install", value: metrics.ready, accent: "good" },
  ];

  return (
    <div className={isCockpit ? "space-y-6 max-w-[1600px] mx-auto w-full" : "space-y-3 max-w-[1300px] mx-auto"}>
      <RoleContextBanner />
      <RoleHandoffInbox />

      {/* ═══ HEADER ═══ */}
      {isCockpit ? (
        <CockpitHeader
          title="Warehouse Command Center"
          subtitle={`Material tracking & execution · ${format(TODAY, "EEEE, MMMM d, yyyy")}`}
          right={
            <>
              <ReportFrictionButton moduleName="Warehouse" />
              <Link to="/warehouse" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5">
                <Package className="h-3.5 w-3.5" /> Full Warehouse
              </Link>
            </>
          }
        />
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-[-0.02em] text-foreground">Warehouse Command Center</h1>
            <p className="text-[12px] text-muted-foreground/70 mt-0.5">
              Material Tracking & Execution · {format(TODAY, "EEEE, MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ReportFrictionButton moduleName="Warehouse" />
            <Link to="/warehouse" className="text-[12px] font-medium text-primary hover:underline inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-muted/30 transition-colors">
              <Package className="h-3.5 w-3.5" /> Full Warehouse
            </Link>
          </div>
        </div>
      )}

      {/* ═══ KPI ROW ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitWhKpis} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          {[
            { label: "Ordered", value: metrics.ordered, icon: Truck, color: metrics.ordered > 0 ? "text-primary" : "text-success", bg: metrics.ordered > 0 ? "bg-primary/10 border-primary/20" : "bg-success/10 border-success/20" },
            { label: "Partial", value: metrics.partial, icon: AlertTriangle, color: metrics.partial > 0 ? "text-warning" : "text-success", bg: metrics.partial > 0 ? "bg-warning/10 border-warning/20" : "bg-success/10 border-success/20" },
            { label: "Received", value: metrics.received, icon: Package, color: metrics.received > 0 ? "text-primary" : "text-success", bg: metrics.received > 0 ? "bg-primary/10 border-primary/20" : "bg-success/10 border-success/20" },
            { label: "Staging", value: metrics.staged, icon: Boxes, color: metrics.staged > 0 ? "text-accent-foreground" : "text-success", bg: metrics.staged > 0 ? "bg-accent/10 border-accent/20" : "bg-success/10 border-success/20" },
            { label: "Ready for Install", value: metrics.ready, icon: CheckCircle, color: "text-success", bg: "bg-success/10 border-success/20" },
          ].map(m => (
            <Card key={m.label} className={`border ${m.bg} shadow-none`}>
              <CardContent className="px-2 py-3 text-center">
                <p className={`text-lg font-extrabold leading-tight ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight mt-1">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ RECEIVING SECTION (ordered lane split into sub-groups) ═══ */}
      {(receivingSubGroups.incoming.length > 0 || receivingSubGroups.partial.length > 0) && (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5 pt-2 border-t border-border/30">
            <div className="flex items-center justify-center h-6 w-6 rounded-md bg-muted/30">
              <Package className="h-3.5 w-3.5 text-primary" />
            </div>
            <div>
              <h2 className="text-[15px] font-extrabold tracking-tight text-foreground">Receiving</h2>
              <p className="text-[10px] text-muted-foreground/60">Verify incoming materials against order package</p>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto text-primary bg-primary/10">
              {receivingSubGroups.incoming.length + receivingSubGroups.partial.length} job{(receivingSubGroups.incoming.length + receivingSubGroups.partial.length) !== 1 ? "s" : ""}
            </span>
          </div>

          {receivingSubGroups.incoming.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1">Incoming / Awaiting Materials</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {receivingSubGroups.incoming.map(renderCard)}
              </div>
            </div>
          )}

          {receivingSubGroups.partial.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-warning uppercase tracking-wider pl-1">Partially Received</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {receivingSubGroups.partial.map(renderCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ OTHER LANES (receiving=fully received, staging, ready) ═══ */}
      {LANE_ORDER.filter(k => k !== "ordered").map(laneKey => {
        const items = lanes[laneKey];
        if (items.length === 0) return null;
        const cfg = LANE_CONFIG[laneKey];
        return (
          <div key={laneKey} className="space-y-3">
            <div className="flex items-center gap-2.5 pt-2 border-t border-border/30">
              <div className="flex items-center justify-center h-6 w-6 rounded-md bg-muted/30">
                {cfg.icon}
              </div>
              <div>
                <h2 className="text-[15px] font-extrabold tracking-tight text-foreground">{cfg.label}</h2>
                <p className="text-[10px] text-muted-foreground/60">{cfg.sublabel}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto ${cfg.badgeColor}`}>
                {items.length} job{items.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {items.map(renderCard)}
            </div>
          </div>
        );
      })}

      {cards.length === 0 && (
        <Card className="border-success/20 bg-success/[0.03]">
          <CardContent className="p-6 text-center">
            <CheckCircle className="h-6 w-6 text-success mx-auto mb-2" />
            <p className="text-sm font-semibold text-success">All Clear</p>
            <p className="text-xs text-muted-foreground mt-1">No materials currently in the warehouse pipeline.</p>
          </CardContent>
        </Card>
      )}

      {/* ═══ INVENTORY INTEGRITY ═══ */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-4 w-4 text-primary" />
          <h2 className="text-[14px] font-bold tracking-tight">Inventory Integrity Today</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[
            { label: "Received Today", value: integrity.receivedToday, color: "text-primary" },
            { label: "Staged Today", value: integrity.allocatedToday, color: "text-accent-foreground" },
            { label: "Installed Today", value: integrity.installedToday, color: "text-success" },
            { label: "Exceptions", value: integrity.exceptions.length, color: integrity.exceptions.length > 0 ? "text-warning" : "text-success" },
          ].map(m => (
            <Card key={m.label} className="border-border/40 bg-card/60 shadow-none">
              <CardContent className="p-3.5 text-center">
                <p className={`text-lg font-extrabold ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider mt-0.5">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {integrity.exceptions.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {integrity.exceptions.map((ex, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[11px] ${
                ex.severity === "critical"
                  ? "border-destructive/20 bg-destructive/[0.03] text-destructive"
                  : "border-warning/20 bg-warning/[0.03] text-warning"
              }`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">{ex.description}</span>
                <span className="ml-auto text-[10px] text-muted-foreground">{ex.jobId}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ RECEIVING MODAL ═══ */}
      <Dialog open={!!receivingModal} onOpenChange={open => { if (!open) { setReceivingModal(null); setReceivingItems([]); } }}>
        {receivingModal && (
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle className="text-[16px] font-bold flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                Verify Materials — Receiving
              </DialogTitle>
            </DialogHeader>

            {/* Job header */}
            <div className="px-4 py-3 rounded-lg border border-border/40 bg-muted/10 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[14px] font-bold text-foreground">{receivingModal.job.customerName}</p>
                  <p className="text-[12px] text-muted-foreground">
                    {receivingModal.job.projectConfig?.bathroomMaterials?.[0] || receivingModal.job.projectType} · {receivingModal.job.address.city}
                  </p>
                </div>
                <div className="text-right">
                  {receivingModal.job.selectedVendor && (
                    <p className="text-[11px] text-muted-foreground">Vendor: <span className="font-semibold text-foreground">{receivingModal.job.selectedVendor}</span></p>
                  )}
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                    {receivingItems.filter(i => i.status !== "pending").length} / {receivingItems.length} verified
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-2 h-1.5 rounded-full bg-muted/30 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300 bg-primary"
                  style={{ width: `${receivingItems.length ? (receivingItems.filter(i => i.status !== "pending").length / receivingItems.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Material list */}
            <div className="flex-1 overflow-y-auto space-y-2 py-2 min-h-0">
              {receivingItems.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">No materials found for this job.</div>
              )}
              {receivingItems.map(item => {
                const statusColors: Record<ReceivingItemStatus, string> = {
                  pending: "border-border/40 bg-card/60",
                  received: "border-success/30 bg-success/[0.04]",
                  missing: "border-warning/30 bg-warning/[0.04]",
                  incorrect: "border-destructive/30 bg-destructive/[0.04]",
                };
                return (
                  <div key={item.id} className={`rounded-lg border p-3 transition-all ${statusColors[item.status]}`}>
                    <div className="flex items-center gap-3">
                      {/* Reference image */}
                      <div className="w-14 h-14 rounded-md border border-border/30 bg-muted/20 flex items-center justify-center shrink-0 overflow-hidden">
                        {item.referenceImageUrl ? (
                          <img src={item.referenceImageUrl} alt={item.name} className="w-full h-full object-cover rounded-md" />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
                        )}
                      </div>

                      {/* Item details */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-foreground truncate">{item.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 font-medium">{item.category}</Badge>
                          <span className="text-[10px] text-muted-foreground">Qty: {item.expectedQty}</span>
                          {item.sku && <span className="text-[10px] text-muted-foreground/50">SKU: {item.sku}</span>}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          size="sm"
                          variant={item.status === "received" ? "default" : "outline"}
                          className={`h-7 text-[10px] px-2.5 ${item.status === "received" ? "bg-success hover:bg-success/90 text-success-foreground" : "border-success/30 text-success hover:bg-success/10"}`}
                          onClick={() => handleSetItemStatus(item.id, item.status === "received" ? "pending" : "received")}
                        >
                          <CheckCircle className="h-3 w-3 mr-0.5" /> Received
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === "missing" ? "default" : "outline"}
                          className={`h-7 text-[10px] px-2.5 ${item.status === "missing" ? "bg-warning hover:bg-warning/90 text-warning-foreground" : "border-warning/30 text-warning hover:bg-warning/10"}`}
                          onClick={() => handleSetItemStatus(item.id, item.status === "missing" ? "pending" : "missing")}
                        >
                          <HelpCircle className="h-3 w-3 mr-0.5" /> Missing
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === "incorrect" ? "default" : "outline"}
                          className={`h-7 text-[10px] px-2.5 ${item.status === "incorrect" ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground" : "border-destructive/30 text-destructive hover:bg-destructive/10"}`}
                          onClick={() => handleSetItemStatus(item.id, item.status === "incorrect" ? "pending" : "incorrect")}
                        >
                          <XCircle className="h-3 w-3 mr-0.5" /> Incorrect
                        </Button>
                      </div>
                    </div>

                    {/* Incorrect reason dropdown */}
                    {item.status === "incorrect" && (
                      <div className="mt-2 ml-[68px]">
                        <Select value={item.incorrectReason || ""} onValueChange={(v) => handleSetIncorrectReason(item.id, v as IncorrectReason)}>
                          <SelectTrigger className="h-7 text-[11px] w-48 bg-card">
                            <SelectValue placeholder="Select reason..." />
                          </SelectTrigger>
                          <SelectContent>
                            {INCORRECT_REASONS.map(r => (
                              <SelectItem key={r.value} value={r.value} className="text-[11px]">{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Notes */}
            <div className="shrink-0 space-y-1.5 pt-2 border-t border-border/30">
              <label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Notes (optional)</label>
              <Textarea
                value={receivingNotes}
                onChange={e => setReceivingNotes(e.target.value)}
                placeholder="Any receiving notes..."
                className="text-[12px] min-h-[50px] bg-card"
              />
            </div>

            {/* Bottom actions */}
            <div className="flex items-center justify-between shrink-0 pt-3 border-t border-border/30">
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-success font-semibold">{receivingItems.filter(i => i.status === "received").length} received</span>
                {receivingItems.filter(i => i.status === "missing").length > 0 && (
                  <span className="text-warning font-semibold">{receivingItems.filter(i => i.status === "missing").length} missing</span>
                )}
                {receivingItems.filter(i => i.status === "incorrect").length > 0 && (
                  <span className="text-destructive font-semibold">{receivingItems.filter(i => i.status === "incorrect").length} incorrect</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-[11px]" onClick={() => { setReceivingModal(null); setReceivingItems([]); }}>
                  Cancel
                </Button>
                {hasIssues && allItemsReviewed && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[11px] font-semibold px-4 border-warning/30 text-warning hover:bg-warning/10"
                    onClick={() => handleConfirmReceived(true)}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                    Mark Partially Received
                  </Button>
                )}
                <Button
                  size="sm"
                  className="text-[11px] font-semibold px-4"
                  disabled={!allReceived}
                  onClick={() => handleConfirmReceived(false)}
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                  {allReceived ? "Mark Fully Received" : "Verify all items"}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* ═══ CALL CONFIRM ═══ */}
      {callConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setCallConfirm(null)}>
          <div className="bg-card border rounded-lg p-6 max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold mb-2">Call {callConfirm.name}?</h3>
            <p className="text-xs text-muted-foreground mb-4">{callConfirm.phone}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCallConfirm(null)} className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted/50">Cancel</button>
              <a href={`tel:${callConfirm.phone.replace(/\D/g, "")}`} className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90">Call Now</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
